import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { apiClient } from '../services/api';

interface SearchResult {
  id: number;
  name: string;
  team: string;
  position: string;
  games: number;
  url: string;
}

interface SearchCache {
  query: string;
  results: SearchResult[];
  timestamp: number;
}

interface PlayerSearchProps {
  onPlayerSelect?: (player: SearchResult) => void;
  placeholder?: string;
  className?: string;
  isMobile?: boolean;
  onMobileClose?: () => void;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const DEBOUNCE_DELAY = 300; // 300ms

interface LocalSearchState {
  query: string;
  results: SearchResult[];
  loading: boolean;
  error: string | null;
}

export const PlayerSearch: React.FC<PlayerSearchProps> = ({ 
  onPlayerSelect,
  placeholder = "Search players...",
  className = "",
  isMobile = false,
  onMobileClose
}) => {
  const [state, setState] = useState<LocalSearchState>({
    query: '',
    results: [],
    loading: false,
    error: null,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [cache, setCache] = useState<Map<string, SearchCache>>(new Map());
  
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const currentRequest = useRef<AbortController | null>(null);

  // Clean up cache periodically
  useEffect(() => {
    const cleanup = () => {
      const now = Date.now();
      setCache(prev => {
        const newCache = new Map(prev);
        newCache.forEach((value, key) => {
          if (now - value.timestamp > CACHE_DURATION) {
            newCache.delete(key);
          }
        });
        return newCache;
      });
    };

    const interval = setInterval(cleanup, 60000); // Clean every minute
    return () => clearInterval(interval);
  }, []);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchPlayers = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setState(prev => ({ ...prev, results: [], loading: false }));
      setIsOpen(false);
      return;
    }

    // Check cache first
    const cached = cache.get(searchQuery.toLowerCase());
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setState(prev => ({ ...prev, results: cached.results, loading: false }));
      setIsOpen(cached.results.length > 0);
      return;
    }

    // Cancel previous request
    if (currentRequest.current) {
      currentRequest.current.abort();
    }

    // Create new request
    currentRequest.current = new AbortController();
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await apiClient.searchPlayers(searchQuery, 8);
      
      if (response.success) {
        const players = response.data || [];
        setState(prev => ({ ...prev, results: players, loading: false }));
        setIsOpen(players.length > 0);
        
        // Update cache
        setCache(prev => new Map(prev).set(searchQuery.toLowerCase(), {
          query: searchQuery,
          results: players,
          timestamp: Date.now()
        }));
      } else {
        setState(prev => ({ 
          ...prev, 
          results: [], 
          loading: false,
          error: 'Search failed'
        }));
        setIsOpen(false);
      }
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('Player search error:', error);
        setState(prev => ({ 
          ...prev, 
          results: [], 
          loading: false,
          error: 'Search failed'
        }));
        setIsOpen(false);
      }
    } finally {
      currentRequest.current = null;
    }
  }, [cache]);

  const debouncedSearch = useCallback((searchQuery: string) => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchPlayers(searchQuery);
    }, DEBOUNCE_DELAY);
  }, [searchPlayers]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setState(prev => ({ ...prev, query: value }));
    setSelectedIndex(-1);

    if (value.length >= 2) {
      debouncedSearch(value);
    } else {
      setState(prev => ({ ...prev, results: [], loading: false }));
      setIsOpen(false);
    }
  };

  const handlePlayerClick = (player: SearchResult) => {
    if (onPlayerSelect) {
      onPlayerSelect(player);
    } else {
      navigate(player.url);
    }
    
    setState(prev => ({ ...prev, query: '', results: [] }));
    setIsOpen(false);
    setSelectedIndex(-1);
    
    if (isMobile && onMobileClose) {
      onMobileClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || state.results.length === 0) {
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
        if (isMobile && onMobileClose) {
          onMobileClose();
        }
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < state.results.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < state.results.length) {
          handlePlayerClick(state.results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        if (isMobile && onMobileClose) {
          onMobileClose();
        }
        break;
    }
  };

  const handleFocus = () => {
    if (state.query.length >= 2 && state.results.length > 0) {
      setIsOpen(true);
    }
  };

  const clearSearch = () => {
    setState(prev => ({ ...prev, query: '', results: [] }));
    setIsOpen(false);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={state.query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={`
            w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg
            bg-white text-gray-900 placeholder-gray-400
            focus:ring-2 focus:ring-green-500 focus:border-green-500
            ${isMobile ? 'text-lg' : 'text-sm'}
          `}
          autoComplete="off"
        />

        {/* Clear Button */}
        {state.query && (
          <button
            onClick={clearSearch}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
          </button>
        )}

        {/* Loading Indicator */}
        {state.loading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-500"></div>
          </div>
        )}
      </div>

      {/* Results Dropdown */}
      {isOpen && (state.results.length > 0 || state.error) && (
        <div
          className={`
            absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg mt-1
            max-h-96 overflow-y-auto
            ${isMobile ? 'text-base' : 'text-sm'}
          `}
        >
          {state.error ? (
            <div className="px-4 py-3 text-red-600 text-center">
              {state.error}
            </div>
          ) : state.results.length === 0 && !state.loading ? (
            <div className="px-4 py-3 text-gray-500 text-center">
              No players found for "{state.query}"
            </div>
          ) : (
            <div className="py-1">
              {state.results.map((player, index) => (
                <button
                  key={player.id}
                  onClick={() => handlePlayerClick(player)}
                  className={`
                    w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors
                    border-b border-gray-100 last:border-b-0
                    ${index === selectedIndex ? 'bg-green-50 text-green-700' : 'text-gray-900'}
                  `}
                >
                  <div className="font-medium">{player.name}</div>
                  <div className={`text-xs mt-0.5 ${
                    index === selectedIndex ? 'text-green-600' : 'text-gray-500'
                  }`}>
                    {player.team} • {player.position}{player.games > 0 ? ` • ${player.games} games` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerSearch;